import { Button, DialogTitle, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text, Divider, useId, makeStyles, useTableFeatures, useTableSelection, createTableColumn, TableColumnDefinition, TableSelectionCell, TableRowId, tokens } from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { useUsersContext } from "../../../contexts/UserContext";
import { useUtilitiesContext } from "../../../contexts/UtilitiesContext";
import BaseUserCombobox from "../../../components/BaseUserCombobox";
import OverlaySpinner from "../../../components/OverlaySpinner";
import { useDeleteUserAssignmentMutation } from "../api/Delete";
import { useUserAssignmentsContext } from "../context/UserAssignmentContext";
import { useUserUnassignmentDialogStore } from "../store/UserUnassignmentDialogStore";
import { useGetUserAssignmentQuery } from "../api/Get";
import { useUserAssignmentGridStore } from "../store/UserAssignmentGridStore";

const useStyles = makeStyles({
    assignRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        marginTop: '10px',
        alignItems: 'center',
    },
    assignLabel: {
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontWeight: 'bold',
    },
    tableWrapper: {
        maxHeight: '250px',
        overflowY: 'auto',
    },
    deviceTable: {
        minWidth: '510px',
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '10px',
        gap: '10px',
    },
    noDataCell: {
        textAlign: "center",
        padding: "32px 0",
        fontSize: "1.2rem",
        fontWeight: "500",
        color: "#888"
    },
    tableHeaderRow: {
        position: "sticky",
        top: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        zIndex: 2,
    }
});

type Item = {
    id: number;
    type: number;
    label: string;
    status: number;
};

const columns: TableColumnDefinition<Item>[] = [
    createTableColumn<Item>({
      columnId: "label",
    }),
    createTableColumn<Item>({
      columnId: "type",
    }),
    createTableColumn<Item>({
      columnId: "status",
    }),
];

export default function UnassignmentInfo() {
    const comboboxId = useId("combobox");
    const styles = useStyles();

    const { userAssignments, listOptions, refetchUserAssignment } = useUserAssignmentsContext();
    const { selectedUserAssignment } = useUserAssignmentGridStore();
    
    const [selectedRows, setSelectedRows] = React.useState(
        () => new Set<TableRowId>()
    );
    
    const { users } = useUsersContext();
    const { close } = useUserUnassignmentDialogStore();
    const [items, setItems] = React.useState<Item[]>([]);

    const { showNotification } = useUtilitiesContext();
    const [userId, setUserId] = useState<string>("");
    const {
        refetch,
        isFetching,
    } = useGetUserAssignmentQuery(
        { userId },
    );
    

    const {
        getRows,
        selection: {
          allRowsSelected,
          someRowsSelected,
          toggleAllRows,
          toggleRow,
          isRowSelected,
        },
      } = useTableFeatures(
        {
          columns,
          items,
          getRowId: (item) => item.id,
        },
        [
          useTableSelection({
            selectionMode: "multiselect",
            onSelectionChange: (_, data) => setSelectedRows(data.selectedItems),
          }),
        ]
    );

    useEffect(() => {
        if (selectedUserAssignment?.userId) {
            setUserId(selectedUserAssignment.userId);
        }
    }, [selectedUserAssignment]);

    useEffect(() => {
        if (userId) getUserAssignmentByUser();
    }, [userId]);

    const rows = getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
          ...row,
          onClick: (e: React.MouseEvent) => toggleRow(e, row.item.id),
          selected,
          appearance: selected ? "brand" : "none",
        };
    });

    
    const getOptionName = React.useCallback((optionId: number, categoryId: number): string => {
        const list = listOptions?.find(o => o.categoryId === categoryId);
        if (list) {
            const option = list.options.find(o => o.id === optionId);
            return option?.name ?? "N/A";
        }
        return "N/A";
    }, [listOptions]);

    const onError = (error: Error) => {
        showNotification({
            type: "error",
            title: "Error",
            message: error.message,
        });
    };

    const deleteUserAssignmentMutation = useDeleteUserAssignmentMutation(
        () => {
            showNotification({
                type: "success",
                title: "Created",
                message: <Text>Unassign devices for user successfully.</Text>,
            });
            close();
            refetchUserAssignment();
        },
        onError
    );

    const getUserAssignmentByUser = async () => {
        const result = await refetch();

        if (result.isError) {
            showNotification({
                type: "error",
                title: "Error",
                message: result.error?.message ?? "Failed to fetch user assignments",
            });
            return; 
        }

        if (result.data?.length && userAssignments?.length) {
            const deviceAssignments: Item[] = result.data?.map((device) => ({
                id: device.deviceId,
                type: device.type,
                label: device.label,
                status: device.status,
            }));

            setItems(deviceAssignments);
        } else {
            setItems([]);
        }
    };

    const onSubmit = () => {
        if (selectedRows.size < 1) {
            showNotification({
                type: "error",
                title: "Error",
                message: "No devices selected.",
            });
            return;
        }

        const selectedDeviceIds = Array.from(selectedRows).filter(
            (id): id is number => typeof id === "number"
        );
        
        deleteUserAssignmentMutation.mutate({
            userId: userId,
            deviceIds: selectedDeviceIds,
        });
    };

    return (
        <>
            <OverlaySpinner open={isFetching} label={"Loading...."} />
            <OverlaySpinner open={deleteUserAssignmentMutation.isPending} label={"Unassigning...."} />

            <DialogTitle>Devices Unassignment</DialogTitle>
            <div className={styles.assignRow}>
                <div className={styles.assignLabel}>Unassign To:</div>
                <BaseUserCombobox
                    id={comboboxId}
                    data={users}
                    defaultValue={userId}
                    disabled={false}
                    onChange={setUserId}
                />
                <Button appearance="primary" onClick={getUserAssignmentByUser} disabled={!userId}>Search</Button>
            </div>
            <Divider appearance="strong" />
            <div className={styles.tableWrapper}>
                <Table aria-label="Default table" className={styles.deviceTable}>
                    <TableHeader className={styles.tableHeaderRow}>
                        {rows.length > 0 ? <TableRow>
                            <TableSelectionCell
                                checked={
                                    allRowsSelected ? true : someRowsSelected ? "mixed" : false
                                  }
                                  onClick={toggleAllRows}
                            />
                            <TableHeaderCell><b>Type</b></TableHeaderCell>
                            <TableHeaderCell><b>Label</b></TableHeaderCell>
                            <TableHeaderCell><b>Status</b></TableHeaderCell>
                        </TableRow>: <></>}
                    </TableHeader>
                    <TableBody>
                        {rows.map(({ item, selected, onClick, appearance }) => (
                            <TableRow key={item.id} onClick={onClick} >
                                <TableSelectionCell checked={selected} />
                                <TableCell>
                                    <TableCellLayout>
                                        {getOptionName(item.type, 2)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {item.label}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {getOptionName(item.status, 9)}
                                    </TableCellLayout>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <div className={styles.actionButtons}>
                <Button onClick={close} appearance="secondary">Close</Button>
                <Button onClick={onSubmit} appearance="primary" disabled={selectedRows.size < 1}>Unassign</Button>
            </div>
        </>
    )
}